/**
 * Hotels-only live search (Hubwayz) — no LDS flights.
 * Reuses destination resolve + mapHubwayzToFlightHotelCards from unified mockup.
 */

import {
  fetchHubwayzDestinations,
  searchHubwayzHotels,
} from "../../services/hubwayzHotelsApi";
import { mapHubwayzToFlightHotelCards } from "../unifiedSearchMockup/mapHubwayzToFlightHotelCards";
import { hideSupplierBrandFromUserText } from "../unifiedSearchMockup/hideSupplierBrandFromUserText";
import { summarizeOccupancy } from "../unifiedSearchMockup/demoFixtures";

function nightsBetween(checkIn, checkOut) {
  try {
    const a = new Date(`${checkIn}T12:00:00`);
    const b = new Date(`${checkOut}T12:00:00`);
    const d = Math.round((b - a) / 86400000);
    return d > 0 ? d : null;
  } catch {
    return null;
  }
}

async function resolveHubwayzDestinationId(dest) {
  if (dest?.hubwayzLocationId) return dest.hubwayzLocationId;
  const country = dest?.countryCode;
  if (!country) return null;
  const res = await fetchHubwayzDestinations(country);
  const list = Array.isArray(res?.destinations) ? res.destinations : [];
  const needle = String(dest?.hubwayzNameHint || dest?.label || "")
    .trim()
    .toLowerCase();
  const hit = list.find((d) => {
    const type = String(d?.type || "city").toLowerCase();
    if (type !== "city") return false;
    return (
      String(d?.seoname || "").toLowerCase() === needle ||
      String(d?.name || "").toLowerCase() === needle
    );
  });
  return hit?.id ?? null;
}

/**
 * @param {object} appliedSearch
 * @param {object} destMeta UNIFIED_DESTINATIONS entry
 */
export async function runHotelsOnlyLiveSearch(appliedSearch, destMeta) {
  const rooms = Array.isArray(appliedSearch?.rooms)
    ? appliedSearch.rooms
    : [{ adults: 2, childAges: [] }];
  const occ = summarizeOccupancy(rooms);
  const nights = nightsBetween(appliedSearch.checkIn, appliedSearch.checkOut);
  const dateRangeLabel =
    appliedSearch.dateRangeLabel ||
    `${appliedSearch.checkIn || ""} → ${appliedSearch.checkOut || ""}`;

  let destinationId = null;
  try {
    destinationId = await resolveHubwayzDestinationId(destMeta);
  } catch (err) {
    return {
      hotelCards: [],
      hubwayzData: null,
      searchRequest: null,
      hotelsError: err?.message || String(err),
      hubwayzDestinationId: null,
      meta: { nights, dateRangeLabel, hotelsCount: 0 },
    };
  }

  if (destinationId == null) {
    return {
      hotelCards: [],
      hubwayzData: null,
      searchRequest: null,
      hotelsError: "לא נמצא יעד ליעד שנבחר",
      hubwayzDestinationId: null,
      meta: { nights, dateRangeLabel, hotelsCount: 0 },
    };
  }

  try {
    const res = await searchHubwayzHotels({
      destinationId,
      checkIn: appliedSearch.checkIn,
      checkOut: appliedSearch.checkOut,
      rooms,
      waitUntilDone: true,
      enrichStatic: true,
    });
    if (!res?.ok) {
      return {
        hotelCards: [],
        hubwayzData: res?.data || null,
        searchRequest: res?.requestPayload || null,
        hotelsError: hideSupplierBrandFromUserText(res?.error, "החיפוש נכשל"),
        hubwayzDestinationId: destinationId,
        meta: { nights, dateRangeLabel, hotelsCount: 0 },
      };
    }
    const cards = mapHubwayzToFlightHotelCards(res.data || {}, {
      destinationId: destMeta?.id,
      dateRangeLabel,
      nights,
      cityLabel: destMeta?.label,
      countryLabel: destMeta?.countryLabel,
      searchRequest: res.requestPayload || null,
      paxCount: Math.max(1, occ.adults + occ.children),
    }).map((c) => ({
      ...c,
      hotelsOnly: true,
      productLabel: "מלון",
      flightOptionsCount: 0,
      outbound: null,
      inbound: null,
    }));

    return {
      hotelCards: cards,
      hubwayzData: res.data || null,
      searchRequest: res.requestPayload || null,
      hotelsError: null,
      hubwayzDestinationId: destinationId,
      meta: {
        nights,
        dateRangeLabel,
        hotelsCount: cards.length,
      },
    };
  } catch (err) {
    return {
      hotelCards: [],
      hubwayzData: null,
      searchRequest: null,
      hotelsError: err?.message || String(err),
      hubwayzDestinationId: destinationId,
      meta: { nights, dateRangeLabel, hotelsCount: 0 },
    };
  }
}
